import { Link2, Loader2, ShieldAlert, Sparkles, Wallet } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useCustodialPanelState } from '@/features/custodial';
// the store and service modules only, not the feature barrel: the barrel pulls
// the onboarding flow back into this module's own dependencies.
import { claimsSocialLink } from '@/features/onboarding/services/onboardingAdmission';
import { getOnboardingRecord } from '@/features/onboarding/services/onboardingStorage';
import { useOnboardingLauncher } from '@/features/onboarding/store';
import { broadcastTradingSession } from '@/features/tradingSession/broadcast';
import {
    anchorLabel,
    useAnchorChain,
    useWallet,
    useWalletNetwork,
} from '@/features/wallet';
import { describeErrorText } from '@/lib/errors';
import { cn, shortenAddress } from '@/lib/utils';

import {
    isOAuthAccountHashInUseError,
    isOAuthBindingRouteMismatchError,
    type OAuthBindingRoute,
} from '../accountBinding';
import { useOAuthSession } from '../hooks/useOAuthSession';
import { useOAuthBindingDialogStore } from '../oauthBindingDialogStore';
import { PROVIDER_NAMES } from '../providerNames';
import { useSignInDialogStore } from '../signInDialogStore';
import {
    completeOAuthAccountBindingWithWallet,
    getAccountHashBinding,
    getOwnedAuthorizationWallet,
    isOAuthAccountHashBoundToOwner,
} from '../walletAccountBinding';

/** Hard stop: this login belongs to somebody else's wallet. */
interface CollisionBlock {
    registeredOwner: string;
}

/** The chain disagrees with the route the user picked. */
interface RouteBlock {
    actualRoute: OAuthBindingRoute;
}

const OAuthAccountBindingDialog: React.FC = () => {
    const pending = useOAuthBindingDialogStore((state) => state.pending);
    const clear = useOAuthBindingDialogStore((state) => state.clear);
    const complete = useOAuthBindingDialogStore((state) => state.complete);
    const openOAuthAccount = useSignInDialogStore(
        (state) => state.openOAuthAccount,
    );
    const launchOnboarding = useOnboardingLauncher((state) => state.launch);
    const { address, isConnected, connect, disconnectWallet } = useWallet();
    const { ensureAnchor } = useWalletNetwork();
    const { anchorChainId } = useAnchorChain();
    const { rebind } = useOAuthSession();
    const openDeposit = useCustodialPanelState((state) => state.openDeposit);
    const [route, setRoute] = useState<OAuthBindingRoute | null>(null);
    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [collision, setCollision] = useState<CollisionBlock | null>(null);
    const [routeBlock, setRouteBlock] = useState<RouteBlock | null>(null);
    // `wallets(owner)` for the connected wallet: null when it owns none, and
    // undefined until the read lands.
    const [ownedWallet, setOwnedWallet] = useState<string | null | undefined>(
        undefined,
    );
    const excludedOwnerConnected =
        !!pending?.excludedOwner &&
        !!address &&
        pending.excludedOwner.toLowerCase() === address.toLowerCase();

    const accountHash = pending?.accountHash;
    useEffect(() => {
        setRoute(null);
        setError(null);
        setCollision(null);
        setRouteBlock(null);
        setOwnedWallet(undefined);
    }, [accountHash]);

    // Preflight the moment a wallet is available. Two facts decide everything
    // that follows: a login already claimed by a different owner is a dead end,
    // and a connected wallet that already owns an account has exactly one usable
    // route, so it is offered directly rather than as a choice the chain would
    // then refuse.
    useEffect(() => {
        if (!accountHash || !address) return;
        let cancelled = false;
        setOwnedWallet(undefined);
        void Promise.allSettled([
            getAccountHashBinding(accountHash),
            getOwnedAuthorizationWallet(address, anchorChainId),
        ]).then(([registration, owned]) => {
            if (cancelled) return;
            if (registration.status === 'fulfilled') {
                const registered = registration.value;
                setCollision((current) => {
                    if (!registered) return null;
                    if (
                        registered.owner.toLowerCase() !== address.toLowerCase()
                    ) {
                        return { registeredOwner: registered.owner };
                    }
                    // switching to the named owner is how the user reaches the
                    // unlink shortcut, so keep the stop screen up; connecting
                    // the owner of a hash we never flagged is just a session
                    // that needs a rebind, which the binding attempt performs.
                    return current;
                });
            }
            // a failed read must not present "set up a new account" to a wallet
            // that already owns one, so it stays unresolved and the chooser
            // stays up with its route-mismatch recovery intact.
            setOwnedWallet(owned.status === 'fulfilled' ? owned.value : null);
        });
        return () => {
            cancelled = true;
        };
    }, [accountHash, address, anchorChainId]);

    // One usable route, so no chooser: the confirmation names the account this
    // login is about to join.
    const preselectedLink =
        !!ownedWallet && !collision && !excludedOwnerConnected;
    useEffect(() => {
        if (preselectedLink) setRoute('existing-wallet');
    }, [preselectedLink]);

    /**
     * The account this login has no home in yet gets created by the main
     * onboarding: it is the only flow that provisions BOTH chains (the binding
     * dialog's own create route stops after Cronos) and the only one that walks
     * the user through launch admission.
     */
    const startAccountSetup = useCallback(() => {
        if (!pending) return;
        const intent = {
            provider: pending.provider,
            accountHash: pending.accountHash,
        };
        clear();
        launchOnboarding('welcome', {
            resumeReauthOnFinish: true,
            associateSocial: intent,
        });
    }, [clear, launchOnboarding, pending]);

    const finishBinding = useCallback(
        async (chosenRoute: OAuthBindingRoute) => {
            if (!pending || !address || !isConnected || isWorking) return;
            if (
                pending.excludedOwner &&
                pending.excludedOwner.toLowerCase() === address.toLowerCase()
            ) {
                setError(
                    'Choose a different wallet. This identity was just unlinked from the connected owner.',
                );
                return;
            }
            setError(null);
            setRouteBlock(null);
            setIsWorking(true);
            try {
                if (!(await ensureAnchor())) {
                    throw new Error(
                        `Switch your connected wallet to ${anchorLabel(anchorChainId)}.`,
                    );
                }
                const result = await completeOAuthAccountBindingWithWallet(
                    address,
                    pending.accountHash,
                    chosenRoute,
                    anchorChainId,
                );
                const rebound = await rebind();
                if (rebound.bindingState !== 'bound') {
                    throw new Error(
                        'The account was updated, but authorization is not visible yet. Please retry.',
                    );
                }

                complete();
                broadcastTradingSession({ type: 'changed' });
                if (result.route === 'new-wallet') {
                    openDeposit();
                }
            } catch (caught) {
                if (isOAuthAccountHashInUseError(caught)) {
                    setCollision({ registeredOwner: caught.registeredOwner });
                    return;
                }
                if (isOAuthBindingRouteMismatchError(caught)) {
                    setRouteBlock({ actualRoute: caught.actualRoute });
                    return;
                }
                try {
                    const recovered = await rebind();
                    if (
                        recovered.bindingState === 'bound' &&
                        (await isOAuthAccountHashBoundToOwner(
                            address,
                            pending.accountHash,
                            anchorChainId,
                        ))
                    ) {
                        complete();
                        broadcastTradingSession({ type: 'changed' });
                        return;
                    }
                } catch {
                    // preserve the original actionable error below
                }
                // a declined wallet prompt is not a failure worth dumping calldata for
                setError(
                    describeErrorText(caught, { operation: 'Account linking' }),
                );
            } finally {
                setIsWorking(false);
            }
        },
        [
            address,
            anchorChainId,
            complete,
            ensureAnchor,
            isConnected,
            isWorking,
            openDeposit,
            pending,
            rebind,
        ],
    );

    const providerName = pending ? PROVIDER_NAMES[pending.provider] : 'social';
    const collisionOwnerConnected =
        !!collision &&
        !!address &&
        collision.registeredOwner.toLowerCase() === address.toLowerCase();
    // a connected wallet whose ownership is still unknown must not be offered a
    // route yet; without one there is nothing to read, so the choice comes first.
    // Once a route is settled the read no longer gates anything.
    const awaitingPreflight = !route && !!address && ownedWallet === undefined;

    // a first-time user still inside the onboarding wizard links the login as
    // one more step of it; this dialog stays out of the way so the two never
    // flash over each other. Everyone else keeps this dialog exactly as is.
    const record = address ? getOnboardingRecord(address) : null;
    const claimedByOnboarding =
        !!record &&
        claimsSocialLink({
            termsAccepted: !!record.termsAcceptedAt,
            locallyCompleted: !!record.completedAt,
        });

    return (
        <Dialog
            open={pending !== null && !claimedByOnboarding}
            onOpenChange={(open) => {
                if (!open && !isWorking) clear();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {collision
                            ? 'This login is already in use'
                            : preselectedLink
                                ? `Add ${providerName} to your account`
                                : route
                                    ? 'Connect your wallet'
                                    : `Set up your ${providerName} login`}
                    </DialogTitle>
                    <DialogDescription>
                        {collision
                            ? `Your ${providerName} login is linked to a Snappy account owned by another wallet.`
                            : preselectedLink
                                ? `The connected wallet already owns a Snappy account, so this login joins it.`
                                : route
                                    ? `Connect the wallet you want to use with this ${providerName} login.`
                                    : `Your ${providerName} identity is not associated with an account yet. Choose how to continue.`}
                    </DialogDescription>
                </DialogHeader>

                {collision ? (
                    <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-muted-foreground">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-destructive">
                            <ShieldAlert size={18} />
                            Linked to another wallet
                        </div>
                        {/* the owner EOA is public on-chain data; naming it is what
                            makes this dead end actionable */}
                        <p className="font-mono text-xs [overflow-wrap:anywhere]">
                            {collision.registeredOwner}
                        </p>
                        <p className="mt-2">
                            {collisionOwnerConnected
                                ? `That is the wallet you have connected. Unlink ${providerName} from it first, then link it to the wallet you meant to use.`
                                : `Connect that wallet and unlink ${providerName} from it before linking this login anywhere else.`}
                        </p>
                    </div>
                ) : awaitingPreflight ? (
                    <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/30 p-4 text-sm text-muted-foreground">
                        <Loader2 size={16} className="animate-spin" />
                        Checking what the connected wallet already owns.
                    </div>
                ) : !route ? (
                    <div className="grid gap-2">
                        {(
                            [
                                [
                                    'setup',
                                    Sparkles,
                                    'Set up a new account',
                                    `Runs the guided setup with the wallet you connect, and links this ${providerName} login to the account it creates.`,
                                ],
                                [
                                    'link',
                                    Link2,
                                    'Link an existing account',
                                    'Connect the wallet that already owns a Snappy account. You approve one Cronos transaction.',
                                ],
                            ] as const
                        ).map(([id, Icon, title, detail]) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() =>
                                    id === 'setup'
                                        ? startAccountSetup()
                                        : setRoute('existing-wallet')
                                }
                                className={cn(
                                    'flex cursor-pointer flex-col items-start gap-1 rounded-xl border border-border bg-secondary/30 p-3 text-left',
                                    'text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-foreground',
                                )}
                            >
                                <Icon size={20} />
                                <span className="text-sm font-semibold text-foreground">
                                    {title}
                                </span>
                                <span className="text-xs">{detail}</span>
                            </button>
                        ))}
                    </div>
                ) : routeBlock ? (
                    <div className="rounded-xl border border-border bg-secondary/30 p-4 text-sm text-muted-foreground">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                            <Wallet size={18} />
                            {routeBlock.actualRoute === 'existing-wallet'
                                ? 'This wallet already has an account'
                                : 'This wallet has no account yet'}
                        </div>
                        {routeBlock.actualRoute === 'existing-wallet'
                            ? `The connected wallet already owns a Snappy account, so a new one cannot be created for it. You can add ${providerName} to the account it already has.`
                            : `The connected wallet does not own a Snappy account, so there is nothing to link. You can set one up with this wallet instead.`}
                    </div>
                ) : (
                    <div className="rounded-xl border border-border bg-secondary/30 p-4 text-sm text-muted-foreground">
                        <div className="mb-2 flex items-center gap-2 font-semibold text-foreground">
                            <Wallet size={18} />
                            {excludedOwnerConnected
                                ? 'Different wallet required'
                                : preselectedLink
                                    ? 'Your account'
                                    : 'Owner wallet required'}
                        </div>
                        {excludedOwnerConnected ? (
                            `This ${providerName} identity was removed from the connected wallet. Choose the wallet you intended to use.`
                        ) : preselectedLink ? (
                            <>
                                <p className="font-mono text-xs [overflow-wrap:anywhere]">
                                    {ownedWallet}
                                </p>
                                <p className="mt-2">
                                    Owned by {shortenAddress(address!)}. You
                                    approve one Cronos transaction that adds
                                    this login to it. Nothing else about the
                                    account changes.
                                </p>
                            </>
                        ) : (
                            'You will approve one Cronos transaction that adds this login to the account this wallet owns.'
                        )}
                    </div>
                )}

                {/* anywhere (not break-word) so an unbroken calldata hex run
                    still shrinks this dialog grid track and leaves the footer
                    buttons on screen */}
                {error && (
                    <p
                        role="alert"
                        className="min-w-0 text-sm text-destructive [overflow-wrap:anywhere]"
                    >
                        {error}
                    </p>
                )}

                <DialogFooter>
                    {collision ? (
                        collisionOwnerConnected ? (
                            <Button
                                type="button"
                                onClick={() => {
                                    clear();
                                    openOAuthAccount();
                                }}
                                className="w-full sm:w-auto"
                            >
                                Manage this login
                            </Button>
                        ) : (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => clear()}
                                className="w-full sm:w-auto"
                            >
                                Close
                            </Button>
                        )
                    ) : awaitingPreflight || !route ? null : routeBlock ? (
                        <Button
                            type="button"
                            disabled={isWorking}
                            onClick={() => {
                                const next = routeBlock.actualRoute;
                                if (next === 'new-wallet') {
                                    // the guided setup owns account creation; the
                                    // dialog's own create route never provisions
                                    // the trading chain.
                                    startAccountSetup();
                                    return;
                                }
                                setRouteBlock(null);
                                setRoute(next);
                                void finishBinding(next);
                            }}
                            className="w-full sm:w-auto"
                        >
                            {routeBlock.actualRoute === 'existing-wallet'
                                ? 'Link to this wallet instead'
                                : 'Set up a new account instead'}
                        </Button>
                    ) : excludedOwnerConnected ? (
                        <Button
                            type="button"
                            disabled={isWorking}
                            onClick={() => void disconnectWallet()}
                            className="w-full sm:w-auto"
                        >
                            Choose a different wallet
                        </Button>
                    ) : !isConnected || !address ? (
                        <Button
                            type="button"
                            onClick={() => void connect()}
                            className="w-full sm:w-auto"
                        >
                            Connect wallet
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            disabled={isWorking}
                            onClick={() => void finishBinding(route)}
                            className="w-full sm:w-auto"
                        >
                            {isWorking && (
                                <Loader2 size={16} className="animate-spin" />
                            )}
                            {preselectedLink
                                ? `Link ${providerName}`
                                : 'Continue with connected wallet'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default OAuthAccountBindingDialog;
